import React from 'react';
import { Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PolicyRoundedIcon from '@mui/icons-material/PolicyRounded';
import WavingHandRoundedIcon from '@mui/icons-material/WavingHandRounded';
import DatasetRoundedIcon from '@mui/icons-material/DatasetRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import CookieRoundedIcon from '@mui/icons-material/CookieRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded';
import LegalPage from './components/LegalPage';

const Text = ({ children }) => (
  <Typography variant="body2" component="p" sx={{ color: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>
    {children}
  </Typography>
);

export default function PrivacyPolicy() {
  const sections = [
    {
      icon: <WavingHandRoundedIcon />,
      title: 'Introduction',
      content: (
        <Text>
          Welcome to FalconXcellence. We respect your privacy and are committed to protecting your
          personal data. This Privacy Policy explains how we collect, use, and safeguard your
          information when you visit our website.
        </Text>
      ),
    },
    {
      icon: <DatasetRoundedIcon />,
      title: 'Information We Collect',
      content: (
        <>
          <Text>We may collect the following types of information:</Text>
          <ul>
            <li>Personal details (e.g., name, email address) if provided voluntarily.</li>
            <li>Technical data such as IP address, browser type, and device information.</li>
            <li>Usage data, including pages visited and time spent on our site.</li>
          </ul>
        </>
      ),
    },
    {
      icon: <TuneRoundedIcon />,
      title: 'How We Use Your Information',
      content: (
        <>
          <Text>The data we collect helps us to:</Text>
          <ul>
            <li>Improve our website and services.</li>
            <li>Respond to inquiries and customer support requests.</li>
            <li>Ensure website security and prevent fraud.</li>
          </ul>
        </>
      ),
    },
    {
      icon: <CookieRoundedIcon />,
      title: 'Cookies & Tracking Technologies',
      content: (
        <Text>
          Our website may use cookies to enhance user experience. You can adjust your browser
          settings to refuse cookies, but some parts of the site may not function properly as a
          result.
        </Text>
      ),
    },
    {
      icon: <HubRoundedIcon />,
      title: 'Third-Party Services',
      content: (
        <Text>
          We may use third-party services for analytics and other functionalities. These third
          parties are subject to their own privacy policies.
        </Text>
      ),
    },
    {
      icon: <LockRoundedIcon />,
      title: 'Data Security',
      content: (
        <Text>
          We take appropriate security measures to protect your personal data, but no method of
          transmission over the internet is 100% secure.
        </Text>
      ),
    },
    {
      icon: <AccountCircleRoundedIcon />,
      title: 'Your Rights',
      content: (
        <>
          <Text>Depending on your location, you may have rights regarding your personal data, such as:</Text>
          <ul>
            <li>Accessing the information we store about you.</li>
            <li>Requesting correction or deletion of your data.</li>
            <li>Opting out of marketing communications.</li>
          </ul>
        </>
      ),
    },
    {
      icon: <UpdateRoundedIcon />,
      title: 'Changes to This Policy',
      content: (
        <Text>
          We may update this Privacy Policy from time to time. We encourage you to review it
          regularly. Continued use of our services implies acceptance of any changes.
        </Text>
      ),
    },
  ];

  return (
    <LegalPage
      icon={<PolicyRoundedIcon />}
      eyebrow="Legal"
      title="Privacy"
      highlight="Policy"
      intro="How FalconXcellence collects, uses, and safeguards your information. Your privacy matters to us, and we keep this simple and transparent."
      lastUpdated="July 7, 2026"
      sections={sections}
      contact={{
        title: 'Questions about your privacy?',
        content: (
          <span>
            If you have any questions about this Privacy Policy, contact us at{' '}
            <Link component="a" href="mailto:info@falconxcellence.com">
              info@falconxcellence.com
            </Link>
            . For more information, see our{' '}
            <Link component={RouterLink} to="/terms">
              Terms &amp; Conditions
            </Link>
            .
          </span>
        ),
      }}
    />
  );
}
